import { ApiRequestError, fetcher, HttpError } from "@/lib/fetcher";
import { ApiResponse } from "./model";

export const ACTION_PAYLOAD_TOO_LARGE = "ACTION_PAYLOAD_TOO_LARGE";
export const MAX_ACTION_UPSERT_BODY_BYTES = 700_000;
export const MAX_PARALLEL_ACTION_UPSERTS = 4;

export class ActionServiceError extends Error {
  readonly code: string;
  readonly status?: number;

  constructor(code: string, message: string, status?: number) {
    super(message);
    this.name = "ActionServiceError";
    this.code = code;
    this.status = status;
  }
}

export function isActionPayloadTooLargeError(error: unknown): boolean {
  return (
    error instanceof ActionServiceError &&
    error.code === ACTION_PAYLOAD_TOO_LARGE
  );
}

// Bytes of the `{"actions":[` + `]}` wrapper around an upsert body.
const ACTION_UPSERT_ENVELOPE_BYTES = '{"actions":[]}'.length;

export function splitActionPayloadBySize(
  payload: CreateActionRequest[],
): CreateActionRequest[][] {
  const encoder = new TextEncoder();
  const batches: CreateActionRequest[][] = [];
  let currentBatch: CreateActionRequest[] = [];
  let currentBytes = ACTION_UPSERT_ENVELOPE_BYTES;

  // Serialize each action once and track the running body size, so batching
  // stays linear in the payload size.
  for (const action of payload) {
    const actionBytes = encoder.encode(JSON.stringify(action)).byteLength;
    if (ACTION_UPSERT_ENVELOPE_BYTES + actionBytes > MAX_ACTION_UPSERT_BODY_BYTES) {
      throw new ActionServiceError(
        ACTION_PAYLOAD_TOO_LARGE,
        `Action "${action.name}" is too large to save. Reduce its descriptions or schema and try again.`,
      );
    }

    // A comma separates the action from the one before it.
    const addedBytes = actionBytes + (currentBatch.length > 0 ? 1 : 0);
    if (
      currentBatch.length > 0 &&
      currentBytes + addedBytes > MAX_ACTION_UPSERT_BODY_BYTES
    ) {
      batches.push(currentBatch);
      currentBatch = [action];
      currentBytes = ACTION_UPSERT_ENVELOPE_BYTES + actionBytes;
    } else {
      currentBatch.push(action);
      currentBytes += addedBytes;
    }
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
}

// --- Types -----------------------------------------------------------------

export interface ActionParameter {
  name: string;
  type: string;
  description: string;
  required: boolean;
  location: string;
  default_value?: unknown;
  example?: unknown;
  // Support for nested structures
  properties?: ActionParameter[]; // For type: object - nested properties
  items?: ActionParameter; // For type: array - item schema
}

export interface ActionAPICall {
  method: string;
  endpoint: string;
  base_url?: string; // Backend may not always return base_url
  headers?: Record<string, string>;
  query_params?: Record<string, string>;
}

export interface ActionLogic {
  type: string;
  api_call?: ActionAPICall;
}

export interface ActionRecord {
  action_id: string;
  system_id: string;
  name: string;
  description: string;
  category?: string;
  tags?: string[];
  logic: ActionLogic;
  input_parameters?: ActionParameter[];
  output_parameters?: ActionParameter[];
  workspace_id?: string;
  organization_id?: string;
  user_id?: string;
  created_time?: string;
  last_updated_time?: string;
  status?: "configured" | "partial" | "error"; // Computed field
}

export interface CreateActionRequest {
  action_id?: string; // Optional: include for upsert to identify existing action
  system_id: string;
  name: string;
  description: string;
  category?: string;
  tags?: string[];
  scopes?: string[];
  logic: ActionLogic;
  input_parameters?: ActionParameter[];
  output_parameters?: ActionParameter[];
}

export interface UpdateActionRequest {
  name?: string;
  description?: string;
  category?: string;
  tags?: string[];
  scopes?: string[];
  logic?: ActionLogic;
  input_parameters?: ActionParameter[];
  output_parameters?: ActionParameter[];
}

export interface GeneratedAction {
  name: string;
  description: string;
  category: string;
  tags: string[];
  scopes?: string[];
  logic: ActionLogic;
  input_parameters: ActionParameter[];
  output_parameters: ActionParameter[];
}

export interface ConvertContentToActionsRequest {
  system_id: string;
  source_type: "file" | "url" | "text";
  content: string;
  file_type?: string;
  selected_server_url?: string;
}

export interface VerifyActionsResult {
  requires_input: boolean;
  server_selections: Array<{
    scope: string;
    required: boolean;
    selected_url?: string | null;
    options: Array<{ url: string; name?: string; description?: string }>;
  }>;
}

export interface ActionGenerationResult {
  system_id: string;
  actions: GeneratedAction[];
  metadata?: Record<string, unknown>;
}

// --- Utility Functions -----------------------------------------------------

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+\-.]*:\/\//i;

function normalizePathname(pathname: string): string {
  const collapsed = pathname.replace(/\/{2,}/g, "/");
  if (!collapsed || collapsed === "/") return "/";
  return collapsed.endsWith("/") ? collapsed.slice(0, -1) : collapsed;
}

function splitPathAndSuffix(value: string): { path: string; suffix: string } {
  const queryIndex = value.indexOf("?");
  const hashIndex = value.indexOf("#");
  const cutCandidates = [queryIndex, hashIndex].filter((index) => index >= 0);
  const cutAt =
    cutCandidates.length > 0 ? Math.min(...cutCandidates) : value.length;

  return {
    path: value.slice(0, cutAt),
    suffix: value.slice(cutAt),
  };
}

function hasSegmentPrefix(path: string, prefix: string): boolean {
  if (path === prefix) return true;
  return path.startsWith(`${prefix}/`);
}

function normalizeApiCallPathOverlap(
  apiCall?: ActionAPICall,
): ActionAPICall | undefined {
  if (!apiCall) return apiCall;

  const baseUrl = apiCall.base_url?.trim();
  const endpointRaw = apiCall.endpoint?.trim();
  if (!baseUrl || !endpointRaw) return apiCall;

  // Avoid rewriting endpoints that are already absolute URLs.
  if (ABSOLUTE_URL_PATTERN.test(endpointRaw)) {
    return apiCall;
  }

  let parsedBaseUrl: URL;
  try {
    parsedBaseUrl = new URL(baseUrl);
  } catch {
    return apiCall;
  }

  const basePath = normalizePathname(parsedBaseUrl.pathname);
  if (basePath === "/") return apiCall;

  const { path: endpointPathRaw, suffix } = splitPathAndSuffix(endpointRaw);
  const endpointPathWithLeadingSlash = endpointPathRaw.startsWith("/")
    ? endpointPathRaw
    : `/${endpointPathRaw}`;
  const endpointPath = normalizePathname(endpointPathWithLeadingSlash);

  if (!hasSegmentPrefix(endpointPath, basePath)) {
    return apiCall;
  }

  const trimmedPath = endpointPath.slice(basePath.length);
  const normalizedEndpointPath =
    trimmedPath.length > 0
      ? trimmedPath.startsWith("/")
        ? trimmedPath
        : `/${trimmedPath}`
      : "/";
  const normalizedEndpoint = `${normalizedEndpointPath}${suffix}`;

  if (normalizedEndpoint === endpointRaw) {
    return apiCall;
  }

  return {
    ...apiCall,
    endpoint: normalizedEndpoint,
  };
}

function normalizeActionApiCall<T extends { logic: ActionLogic }>(
  action: T,
): T {
  const normalizedApiCall = normalizeApiCallPathOverlap(action.logic?.api_call);
  if (!normalizedApiCall || normalizedApiCall === action.logic?.api_call) {
    return action;
  }

  return {
    ...action,
    logic: {
      ...action.logic,
      api_call: normalizedApiCall,
    },
  };
}

/**
 * Compute the status of an action based on its data
 * - "configured": Action is fully set up with all required fields
 * - "partial": Action exists but is missing some information
 * - "error": Action has critical issues (missing endpoint or method)
 */
export function computeActionStatus(
  action: ActionRecord,
): "configured" | "partial" | "error" {
  // Check for critical errors
  if (!action.logic?.api_call?.method || !action.logic?.api_call?.endpoint) {
    return "error";
  }

  // Check if action has all recommended fields
  const hasInputParams =
    action.input_parameters && action.input_parameters.length > 0;
  const hasOutputParams =
    action.output_parameters && action.output_parameters.length > 0;
  const hasDescription =
    action.description && action.description.trim().length > 0;

  // Configured: has method, endpoint, description, and at least input or output params
  if (hasDescription && (hasInputParams || hasOutputParams)) {
    return "configured";
  }

  // Partial: has method and endpoint but missing other fields
  return "partial";
}

// --- API Functions ---------------------------------------------------------

/**
 * Normalize sort order to "1" (asc) or "-1" (desc)
 */
function normalizeSortOrder(order?: string): string | undefined {
  if (!order) return undefined;
  const lowerOrder = order.toLowerCase();
  if (lowerOrder === "asc" || lowerOrder === "1") {
    return "1";
  }
  if (lowerOrder === "desc" || lowerOrder === "-1") {
    return "-1";
  }
  return undefined;
}

/**
 * Fetch all actions for a system
 */
export async function fetchActionsBySystemId(
  systemId: string,
  sortField?: string,
  sortOrder?: string,
  limit?: number,
  offset?: number,
): Promise<ActionRecord[]> {
  const params = new URLSearchParams({
    system_id: systemId,
  });
  if (sortField) {
    params.set("sort_field", sortField);
  }
  const normalizedOrder = normalizeSortOrder(sortOrder);
  if (normalizedOrder) {
    params.set("sort_order", normalizedOrder);
  }
  if (limit) {
    params.set("limit", limit.toString());
  }
  if (offset) {
    params.set("offset", offset.toString());
  }

  const response = await fetcher<ApiResponse<ActionRecord[]>>(
    `/api/ai-transform/v1/action?${params.toString()}`,
  );

  const actions = response.data ?? [];
  return actions.map(normalizeActionApiCall);
}

/**
 * Fetch a single action by ID
 */
export async function fetchActionById(
  actionId: string,
): Promise<ActionRecord | null> {
  const response = await fetcher<ApiResponse<ActionRecord[]>>(
    `/api/ai-transform/v1/action?action_id=${encodeURIComponent(actionId)}`,
  );

  if (response.status === "OK" && response.data?.[0]) {
    return normalizeActionApiCall(response.data[0]);
  }

  return null;
}

/**
 * Create a new action
 */
export async function createAction(
  payload: CreateActionRequest,
): Promise<ActionRecord> {
  const response = await fetcher<ApiResponse<ActionRecord[]>>(
    "/api/ai-transform/v1/action",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }

  throw new Error(response.message || "Failed to create action");
}

/**
 * Create multiple actions in bulk
 */
export async function createActions(
  payload: CreateActionRequest[],
): Promise<ActionRecord[]> {
  const response = await fetcher<ApiResponse<ActionRecord[]>>(
    "/api/ai-transform/v1/action/bulk",
    {
      method: "POST",
      body: JSON.stringify({ actions: payload }),
    },
  );

  if (response.status === "OK" && response.data) {
    return response.data;
  }

  throw new Error(response.message || "Failed to create actions");
}

/**
 * Update an existing action
 */
export async function updateAction(
  actionId: string,
  updates: UpdateActionRequest,
): Promise<ActionRecord> {
  const response = await fetcher<ApiResponse<ActionRecord[]>>(
    `/api/ai-transform/v1/action?action_id=${encodeURIComponent(actionId)}`,
    {
      method: "PUT",
      body: JSON.stringify(updates),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }

  throw new Error(response.message || "Failed to update action");
}

/**
 * Upsert multiple actions in bulk
 */
export async function upsertActions(
  payload: CreateActionRequest[],
): Promise<ActionRecord[]> {
  let response: ApiResponse<ActionRecord[]>;
  try {
    response = await fetcher<ApiResponse<ActionRecord[]>>(
      "/api/ai-transform/v1/action/upsert",
      {
        method: "PUT",
        body: JSON.stringify({ actions: payload }),
      },
    );
  } catch (error) {
    if (error instanceof HttpError && error.status === 413) {
      throw new ActionServiceError(
        ACTION_PAYLOAD_TOO_LARGE,
        "The imported collection is too large to save. Please reduce the descriptions or split the collection.",
        error.status,
      );
    }
    throw error;
  }

  if (response.status === "OK" && response.data) {
    return response.data;
  }

  throw new Error(response.message || "Failed to upsert actions");
}

/**
 * Delete an action
 */
export async function deleteAction(actionId: string): Promise<void> {
  const response = await fetcher<ApiResponse<unknown>>(
    `/api/ai-transform/v1/action?action_id=${encodeURIComponent(actionId)}`,
    {
      method: "DELETE",
    },
  );

  if (response.status !== "OK") {
    throw new Error(response.message || "Failed to delete action");
  }
}

/**
 * Preview actions from uploaded content (dry-run, no DB save)
 * Converts file/URL/text content to actions for user review before saving
 */
export async function verifyActions(request: {
  source_type: "file" | "url" | "text";
  content: string;
  file_type?: string;
  file_size?: number;
}): Promise<VerifyActionsResult | null> {
  const response = await fetcher<ApiResponse<VerifyActionsResult[]>>(
    "/api/ai-transform/v1/system/verify",
    { method: "POST", body: JSON.stringify(request) },
  );
  if (response.status === "OK") return response.data?.[0] ?? null;
  throw new Error(response.message || "Failed to verify action content");
}

export async function previewActionsFromContent(
  request: ConvertContentToActionsRequest,
): Promise<ActionGenerationResult> {
  const response = await fetcher<ApiResponse<ActionGenerationResult[]>>(
    "/api/ai-transform/v1/action/process",
    {
      method: "POST",
      body: JSON.stringify(request),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    const result = response.data[0];
    if (!result.actions?.length) {
      throw new ApiRequestError(
        response.message || "No valid API endpoints found",
        "NO_VALID_ENDPOINTS",
      );
    }
    return {
      ...result,
      actions: (result.actions || []).map(normalizeActionApiCall),
    };
  }

  throw new ApiRequestError(
    response.message || "Failed to preview actions from content",
    response.error_code,
    response.status === "INVALID" ? 400 : undefined,
  );
}

/**
 * Batch create multiple actions from generated actions
 * Implements upsert logic: if action with same endpoint+method exists, update it; otherwise create new
 */
export async function batchCreateActions(
  systemId: string,
  actions: GeneratedAction[],
): Promise<ActionRecord[]> {
  const results: ActionRecord[] = [];

  // Queue valid actions for bulk upsert.
  const actionsToUpdate: CreateActionRequest[] = [];

  for (const action of actions.map(normalizeActionApiCall)) {
    const method = action.logic?.api_call?.method?.toUpperCase() || "";
    const endpoint = action.logic?.api_call?.endpoint || "";

    if (!method || !endpoint) {
      console.warn(
        `Skipping action "${action.name}" - missing method or endpoint`,
      );
      continue;
    }

    // if (existingAction) {
    // Add to update list with action_id for upsert
    actionsToUpdate.push({
      system_id: systemId,
      name: action.name,
      description: action.description,
      category: action.category,
      tags: action.tags,
      scopes: action.scopes,
      logic: action.logic,
      input_parameters: action.input_parameters,
      output_parameters: action.output_parameters,
    });
    //   } else {
    //     // Add to create list
    //     actionsToCreate.push({
    //       system_id: systemId,
    //       name: action.name,
    //       description: action.description,
    //       category: action.category,
    //       tags: action.tags,
    //       logic: action.logic,
    //       input_parameters: action.input_parameters,
    //       output_parameters: action.output_parameters,
    //     });
    //   }
  }

  // Bulk create new actions
  // if (actionsToCreate.length > 0) {
  //   try {
  //     console.log(`Creating ${actionsToCreate.length} new actions in bulk`);
  //     const createdActions = await createActions(actionsToCreate);
  //     results.push(...createdActions);
  //   } catch (error) {
  //     console.error("Failed to bulk create actions:", error);
  //   }
  // }

  // Update existing actions (still need to do one by one)
  // for (const { actionId, updates } of actionsToUpdate) {
  //   try {
  //     console.log(`Updating existing action: ${actionId}`);
  //     const result = await updateAction(actionId, updates);
  //     results.push(result);
  //   } catch (error) {
  //     console.error(`Failed to update action "${actionId}":`, error);
  //   }
  // }
  if (actionsToUpdate.length > 0) {
    try {
      const batches = splitActionPayloadBySize(actionsToUpdate);
      console.log(
        `Updating ${actionsToUpdate.length} actions in ${batches.length} payload-sized batch(es)`,
      );

      const resultsByBatch: ActionRecord[][] = Array.from({
        length: batches.length,
      });
      let nextBatchIndex = 0;
      let firstError: unknown;

      const worker = async () => {
        while (firstError === undefined) {
          const batchIndex = nextBatchIndex++;
          if (batchIndex >= batches.length) return;

          const batch = batches[batchIndex];
          console.log(
            `Updating action batch ${batchIndex + 1}/${batches.length} (${batch.length} actions)`,
          );

          try {
            resultsByBatch[batchIndex] = await upsertActions(batch);
          } catch (error) {
            firstError = error;
          }
        }
      };

      const workerCount = Math.min(MAX_PARALLEL_ACTION_UPSERTS, batches.length);
      await Promise.all(Array.from({ length: workerCount }, worker));

      if (firstError !== undefined) {
        throw firstError;
      }

      results.push(...resultsByBatch.flat());
    } catch (error) {
      console.error("Failed to bulk update actions:", error);
      throw error;
    }
  }

  return results;
}
